import type { Review } from "../../../api/content/reviewDecoders";

/**
 * What the curator does on a review page before deciding, besides the marks the server
 * keeps (curation-ui.md CU-5): whether they acknowledge what is open, and whether the
 * technical details show. Tied to one head: a new head clears the acknowledgement,
 * because it was about content that changed (R-1).
 */
export interface ReviewPageState {
  readonly head: string | null;
  readonly acknowledged: boolean;
  readonly technical: boolean;
}

export type ReviewPageEvent =
  | { readonly type: "headLoaded"; readonly head: string }
  | { readonly type: "acknowledgementChanged"; readonly acknowledged: boolean }
  | { readonly type: "technicalToggled" };

export const INITIAL_REVIEW_PAGE_STATE: ReviewPageState = { head: null, acknowledged: false, technical: false };

export function reviewPageReducer(state: ReviewPageState, event: ReviewPageEvent): ReviewPageState {
  switch (event.type) {
    case "headLoaded":
      return event.head === state.head ? state : { ...state, head: event.head, acknowledged: false };
    case "acknowledgementChanged":
      return { ...state, acknowledged: event.acknowledged };
    case "technicalToggled":
      return { ...state, technical: !state.technical };
  }
}

/** Findings that stand in the way of a silent merge: conflicts, failing or missing checks,
 * questions the change brings that check nothing, open possible duplicates over the review threshold. */
export function blockingCount(review: Review): number {
  const open = review.openFindings;
  return (
    open.conflictFindingCount +
    open.failingCheckIds.length +
    open.notRunnableCheckIds.length +
    open.uncheckedQuestionIds.length +
    open.openCandidateCount +
    (open.checkRunMissing ? 1 : 0) +
    (open.conflictCheckMissing ? 1 : 0)
  );
}

/** Whether the merge button may be pressed: module B allows the caller's merge of the head shown, acknowledged if needed. */
export function mergeReady(review: Review, state: ReviewPageState): boolean {
  return (
    review.actions.some((served) => served.action === "merge" && served.refusal === null) &&
    state.head === review.head &&
    (!review.openFindings.needsAcknowledgement || state.acknowledged)
  );
}
